import type {
  BuyerResearchFlagValue,
  BuyerResearchPullBlocker,
  BuyerResearchRefinementBlocker,
  BuyerResearchRefinementKind,
  BuyerResearchRefinementStatus,
  BuyerResearchRunStatus,
  BuyerResearchSellerFinancing,
  BuyerResearchSourceOutcome,
  BuyerResearchUnscoredReason,
} from "@/api/buyerResearch";

/**
 * Every user-facing string on the Research tab, in one place.
 *
 * <p>The tab, the brief drawer, the run history and the candidate cards share a
 * vocabulary — "attach", "dismiss", "recommended", the three flag values — and
 * a card saying "Attach" beside a confirm saying "Tie" is the kind of drift a
 * second copy home invites. Tests import these rather than retyping them, so a
 * reworded sentence fails nothing that asserts only the behaviour.</p>
 */
/**
 * A short list as one line: every item up to `max`, then a count for the rest.
 *
 * <p>The cap is what keeps a folded summary to ONE line. A buyer with eight
 * industries would otherwise wrap the summary to three lines and the fold would
 * have bought nothing, which is the whole point of folding.</p>
 */
function joinCapped(items: readonly string[], max: number): string {
  if (items.length <= max) return items.join(", ");
  return `${items.slice(0, max).join(", ")} and ${items.length - max} more`;
}

/**
 * 🔴 R1/R15 — the sentence for rows a run BOUGHT and never scored, one per
 * reason code the server sends on a source-log line.
 *
 * <p>`satisfies Record<BuyerResearchUnscoredReason, …>` is the point: a member
 * added to the union without a sentence here fails typecheck, instead of
 * quietly falling through to the generic count.</p>
 *
 * <p>🔴 <b>Clef records and model dollars never share a sentence (R2).</b> The
 * two record-side reasons say the rows still counted against Clef's daily
 * allowance; the model-side one says the run's AI budget is used, and nothing
 * else.</p>
 */
const UNSCORED_SENTENCES = {
  /** R1 — a scoring chunk failed twice. The rows were bought. */
  "chunk failed": (count: number) =>
    `${count} could not be scored — all of them still counted against the day's allowance.`,
  /** R2 — the pull met the run's AI budget line before this chunk. */
  "spend cap reached": (count: number) =>
    `${count} could not be scored — this run's AI budget is used.`,
  /** RG24 — the run, pull or refinement reached its time limit before the
   * chunk holding these rows started. The rows were bought. */
  "time limit": (count: number) =>
    `${count} could not be scored in time — all of them still counted against the day's allowance.`,
  /** The daily AI ceiling or a cancel stopped the run before the chunk holding
   * these rows started. The rows were bought. */
  "run stopped": (count: number) =>
    `${count} could not be scored — the run stopped before it reached them. All of them still counted against the day's allowance.`,
} satisfies Record<BuyerResearchUnscoredReason, (count: number) => string>;

/**
 * How each reason joins the pull's batch line, which already ends on the
 * allowance clause. A RECORDS reason becomes one more `·` part of that same
 * sentence (so the allowance is not said twice), with any explanation after
 * it; a MODEL reason is a sentence of its own, so the two budgets stay apart.
 */
const UNSCORED_IN_BATCH_LINE = {
  "chunk failed": { budget: "records", clause: "could not be scored", after: null },
  "spend cap reached": { budget: "model", clause: "could not be scored", after: null },
  "time limit": { budget: "records", clause: "could not be scored in time", after: null },
  "run stopped": {
    budget: "records",
    clause: "could not be scored",
    after: "The run stopped before it reached them.",
  },
} satisfies Record<
  BuyerResearchUnscoredReason,
  { budget: "records" | "model"; clause: string; after: string | null }
>;

/** The reason as one this client has a sentence for, or null. Read by MEMBERSHIP
 * of the typed map, never by guessing at the text. */
function knownUnscoredReason(reason: string | null): BuyerResearchUnscoredReason | null {
  return reason != null && Object.prototype.hasOwnProperty.call(UNSCORED_SENTENCES, reason)
    ? (reason as BuyerResearchUnscoredReason)
    : null;
}

/** Any other reason: the count, with no clause. A reason this client has no
 * sentence for is not paraphrased. */
const UNSCORED_GENERIC = (count: number) => `${count} could not be scored.`;

function unscoredSentenceFor(count: number, reason: BuyerResearchUnscoredReason | null): string {
  return reason != null ? UNSCORED_SENTENCES[reason](count) : UNSCORED_GENERIC(count);
}

export const researchCopy = {
  tab: {
    emptyTitle: "No research runs yet",
    emptyDescription:
      "Full research searches the public listing sites for businesses that fit this buyer's brief, reads the matches, and scores each one against what the buyer told you. The search itself costs no AI spend — only the scoring does — and a run usually finishes in a couple of minutes.",
    emptyDescriptionReadOnly:
      "Their consultant has not run a listing search yet. Full research searches the public listing sites for businesses that fit this buyer's brief and scores each match against what the buyer said.",
    intro: (buyerName: string) =>
      `Listings Phoenix found for ${buyerName}. The search costs no AI spend — only scoring the matches does.`,
    /** The full search, built from the buyer's brief. Named apart from Quick
     *  search so the two buttons beside the search box cannot be read as one
     *  control: with only one button there, a specialist read it as the box's
     *  own submit and started a full run from a typed sentence. */
    action: "Full research",
    actionPending: "Building the brief…",
    briefFailed: "Couldn't build the search brief.",
    loadFailedWhat: "this buyer's research runs",
    runLoadFailedWhat: "this research run",
    historyHeading: "Run history",
    selectedHeading: "Selected run",
    briefHeading: "Search brief",
    sourcesHeading: "Sources",

    /**
     * RB8 — the skipped sources collapse behind this one line.
     *
     * <p>The count is DERIVED, never written down. The reason is folded in only
     * when every skipped source gave the same one — which is the default
     * deployment's case, where the web-search sources are switched off
     * together — so this line can never speak for a source it does not
     * describe. With mixed reasons the count stands alone and each row carries
     * its own on expand.</p>
     */
    skippedSummary: (count: number, reason: string | null) => {
      const head = `${count} source${count === 1 ? "" : "s"} skipped`;
      return reason ? `${head} — ${reason}` : head;
    },
    skippedShow: "Show skipped",
    skippedHide: "Hide skipped",
    resultsHeading: "Candidates",
    cancel: "Cancel run",
    cancelPending: "Cancelling…",
    cancelRequested: "Cancel requested",
    elapsedPrefix: "Running for",
    queued: "Waiting for a worker",
    /**
     * RG23 — the one progress sentence for a batch being scored, used by a
     * first run AND by a pull, so the two cannot describe the same thing in two
     * ways. It counts up a group of twenty at a time as the worker saves each
     * group. "Appear here" is literal: each group lands on the list below
     * without a refresh, and the rows still waiting are never shown.
     */
    scoringProgress: (done: number, planned: number) =>
      `Scoring — ${done} of ${planned} done. The rest will appear here as they're scored.`,
    statedPriceBandPrefix: "Stated price band:",
    statedPriceBandNote: "Context only — it does not feed the search.",
    terminalReasonPrefix: "Stopped:",

    /**
     * 🔴 <b>THE RUN'S OWN SPEND, STATED ONCE, ABOVE THE LIST.</b>
     *
     * <p>There was no run-level spend figure above the results at all. What a
     * run had cost could be read off the history rail (for a run you had
     * selected from it), or summed out of the per-source rows, or — the only
     * sentence that named a budget — off the pull control at the FOOT of the
     * page, which renders nothing until a pull is available. So the reader who
     * had just spent the money was the one who could not see the number.</p>
     *
     * <p>This is the RUN's accumulated spend (`run.estimatedCostUsd`), which is
     * correct from the moment the run settles and stays correct as pulls and
     * refinements add to it. It is deliberately NOT a budget: what is left to
     * spend belongs beside the control that offers to spend it, which is RC2's
     * rule and the pull control's own job.</p>
     */
    runSpend: (cost: string) => `${cost} spent on this run`,
    /**
     * ── THE RUN BAR (WS5) ────────────────────────────────────────
     *
     * <p>Run history was an 18rem column on `lg:grid-cols-[18rem_1fr]` — a
     * VIEWPORT breakpoint deciding the layout of a container that is not the
     * viewport, which is the Table &amp; Tile §12 violation the standard exists
     * for: inside the buyer page's own content column the two-track grid
     * applies at 1024px of WINDOW while the panel itself is ~704px, so the
     * history took 288px of it and the run body got the rest.</p>
     *
     * <p>It collapses into one control on the run bar instead. The bar names
     * which run is on screen; pressing it drops the full list beneath. Picking
     * a row remounts the run body (it is keyed on the run id), which closes the
     * list with no explicit close.</p>
     */
    runPickerPosition: (index: number, total: number) => `Run ${index} of ${total}`,

    /**
     * \U0001F534 THE DISCLOSURE IS OFFERED ON A SINGLE RUN TOO, and the reason is
     * that it is not only a chooser. The history ROW carries facts the bar does
     * not \u2014 who requested the run, how many sources answered of how many were
     * attempted, how many Clef records the allowance was spent on and what the
     * free count call said the filter matched \u2014 so withholding it from a buyer
     * with one run would make those unreachable rather than merely
     * inconvenient. "Run 1 of 1" IS suppressed: a position is a fact about a
     * choice, and there is none.
     */
    runPickerShow: "Show run history",
    runPickerHide: "Hide run history",

    /**
     * ── THE ACCOUNT OF THE SEARCH (WS5) ─────────────────────────────
     *
     * <p>The brief, the query it became, and the per-source log are one story
     * told in order, and a specialist reads it ONCE — on the run they are
     * chasing, not on every run they open. Three sections became one
     * disclosure.</p>
     *
     * <p>🔴 IT IS NOT COLLAPSED UNCONDITIONALLY, and the exception is a recorded
     * owner ruling rather than a nicety: <i>CompletedPartial's ONLY account of
     * itself is the per-source error</i>, which lives inside the source log. A
     * run with a failed source therefore opens the account by default AND says
     * so on the summary line in error tone. A run still IN FLIGHT opens it too,
     * because there the source log is the progress list and the only thing on
     * screen.</p>
     */
    accountHeading: "How this search was built",
    accountShow: "Show",
    accountHide: "Hide",
    accountSummarySettled: (sources: number) =>
      `The brief, the query it became, and ${sources} source${sources === 1 ? "" : "s"}.`,
    accountSummaryWorking: "The sources, as they answer.",
    accountFailureNote: (failed: number) =>
      `${failed} source${failed === 1 ? "" : "s"} failed — the reason is in the log below.`,

    noCandidates: "No candidates were shown for this run.",
    noCandidatesHint:
      "Every listing the sources returned was already on this buyer's list, dismissed, or scored too low to show.",
  },

  drawer: {
    title: "Search brief",
    subtitle: "Check what Phoenix proposed, correct anything the buyer would, then run.",
    run: "Run",
    running: "Queuing…",
    cancel: "Cancel",
    industries: "Industries",
    industriesAdd: "Add an industry",
    industriesPlaceholder: "e.g. Plumbing",
    industriesAddButton: "Add",
    industryRemove: (name: string) => `Remove ${name}`,

    // ---- resolved industry codes (D6 / RA6) -----------------------------
    industryCodes: "Industry codes",
    industryCodesHint:
      "Clef filters on these codes rather than on the words. Prune anything that does not belong — a term you leave with no codes still counts as an industry, it just stops narrowing the search by code.",
    industryCodeRemove: (code: string, term: string) => `Remove ${code} from ${term}`,
    /** Clef was asked and matched nothing — its own words for why. */
    industryCodeUnresolved: (reason: string | null) =>
      reason ?? "Clef matched no codes for this term.",
    /** Every suggestion pruned by a person. Distinct from the line above, and the
     * distinction is the point: this one is an answer somebody gave. */
    industryCodesPruned: "No codes — this term will not narrow the search.",
    industryCodeVia: (via: string) => `matched on ${via}`,
    geographies: "Geographies",
    geographyState: "State",
    geographyMetro: "Metro",
    geographyAdd: "Add a geography",
    geographyRemove: (index: number) => `Remove geography ${index + 1}`,
    willRelocate: "Will relocate",
    willRelocateYes: "Yes",
    willRelocateNo: "No",
    willRelocateUnknown: "Not known",
    askingPriceMin: "Asking price — minimum",
    askingPriceMax: "Asking price — maximum",
    cashFlowMin: "Cash flow — minimum",
    revenueMin: "Revenue — minimum",
    sellerFinancing: "Seller financing",
    exclusions: "Exclusions",
    exclusionsPlaceholder: "Anything the buyer will not consider",
    notes: "Notes",
    notesPlaceholder: "Anything else the search should know",
    moneyPlaceholder: "Not set",

    // ---- the stepped brief (WS4) ----------------------------------------
    // 🔴 THE STEPS ARE GROUPED BY WHAT A FIELD DOES, NOT WHERE IT CAME FROM
    // (R4). That is why `exclusions` sits beside `notes` on the first step and
    // not with the money floors: neither narrows the search, so grouping them
    // with things that do would tell a specialist they were filtering when
    // they were not.
    stepLooking: "What they're looking for",
    stepSpend: "What they can spend",
    stepWhere: "Where",
    stepAsk: "Ask Phoenix",
    stepReview: "Review and run",

    /** The rail's aria-label. It is a navigation list, not a progress bar. */
    stepsLabel: "Brief steps",
    stepGoTo: (label: string) => `Go to ${label}`,
    stepNeedsAttention: "Needs attention",

    back: "Back",
    next: "Next",

    /** R4 — the pair, labelled so nobody reads it as a filter. */
    contextPair: "Notes and exclusions",
    contextPairHint:
      "Context Phoenix uses when ranking. Neither narrows the search.",

    /**
     * 🔴 RA2 — a brief that says the buyer will relocate sends NO states and no
     * cities at all. The geography rows stay on screen and stop reaching the
     * vendor, which is invisible unless somebody says so here.
     */
    relocateIgnoresGeography:
      "Because the buyer will relocate, Phoenix searches the whole market: the states and metros below are not sent. Change the relocation answer to search them.",

    // ---- the rail's value summaries -------------------------------------
    // A rail that carries only labels is a progress bar. These are what make
    // it worth clicking back to a step you have already done.
    summaryNoIndustries: "Every industry",
    summaryNoGeographies: "Anywhere",
    summaryRelocating: "Anywhere — will relocate",
    summaryNoMoney: "No price or floor set",
    summaryAskNotStarted: "Not started",
    summaryAskTurns: (turns: number) =>
      turns === 1 ? "1 thing you asked for" : `${turns} things you asked for`,
    summaryCount: (n: number) => (n === 1 ? "1 match" : `${n.toLocaleString()} matches`),
    summaryReviewReady: "Ready to run",
    summaryReviewMoney: "A money field will not parse",

    // ---- step 5, the read-only review ------------------------------------
    /**
     * 🔴 R11(2) — every field, read-only, each row clicking through to its own
     * step. A review that shows a subset invites a specialist to trust that
     * what it omits is empty.
     */
    reviewHint: "Everything the run will be given. Click any row to go back and change it.",
    /**
     * 🔴 THE BAND IS ON THE REVIEW BECAUSE THE RUN IS GIVEN IT.
     *
     * <p>`fromDraft` carries `statedPriceBand` straight onto the brief, so
     * `reviewHint` above — "everything the run will be given" — was false
     * while this row was missing. It does not feed the SEARCH, which is what
     * its own caption on the spend step says and why it is easy to read as
     * not worth showing; "given to the run" and "narrows the query" are
     * different claims, and the review makes the first one.</p>
     */
    reviewStatedPriceBand: "Stated price band",
    reviewEmpty: "Not set",
    reviewEdit: (label: string) => `Change ${label}`,

    // ---- the unsaved-changes guard ---------------------------------------
    // 🔴 NEITHER `Modal` NOR `ResourceDrawer` HAS A BLOCKED-CLOSE PATH — every
    // close route funnels through one `onClose`, which is what makes a
    // caller-side guard complete rather than a compromise.
    discardTitle: "Leave this brief?",
    discardMessage:
      "The edits you have made here are not saved anywhere. Leaving loses them. The conversation itself is kept and will be offered again next time.",
    discardConfirm: "Leave and lose the edits",
    discardCancel: "Stay",

    // ---- the read-only panel's fold (R5) --------------------------------
    // 🔴 The fold is `SourceLog`'s rule, and the copy is shaped to match it:
    // one derived summary line that stands in for the rows, plus a toggle. See
    // `ResearchBriefReadOnly` for the rule itself and for what counts as
    // divergent.
    briefShow: "Show the brief",
    briefHide: "Hide the brief",

    /**
     * The one line that stands in for a folded brief: what the run was narrowed
     * to, and nothing else.
     *
     * <p>Industries and locations, because those two answer "why these
     * listings?" — the question a reader has BEFORE they open the panel. The
     * money bounds are deliberately not here: they are the reason somebody
     * opens it, so putting them in the summary removes the reason to.</p>
     *
     * <p>Both lists are capped. A summary that wraps is not a summary.</p>
     */
    briefSummary: (industries: readonly string[], locations: readonly string[]): string => {
      const parts: string[] = [];
      if (industries.length > 0) parts.push(joinCapped(industries, 3));
      if (locations.length > 0) parts.push(joinCapped(locations, 2));
      // Neither is a real state — a brief with no industry and no location
      // searched the whole market, and saying so is more use than an empty row.
      return parts.length > 0 ? parts.join(" · ") : "No industries or locations were recorded.";
    },

    /**
     * 🔴 D3(c) — the hedge on everything above it.
     *
     * <p>`BriefJson` is written ONCE, when the run is queued
     * (`BuyerResearchService.cs:229`), and there is no update path. The
     * provenance labels were resolved against the buyer record as it stood at
     * that moment, so a field corrected on the record afterwards — or a query
     * narrowed in conversation — leaves this panel describing a source that is
     * no longer where the value would come from today. The panel is a
     * SNAPSHOT, and nothing else on it says so.</p>
     *
     * <p>Two arms because the timestamp is not on the brief: it belongs to the
     * run that persisted it, and a caller that cannot supply one still gets the
     * hedge. Saying the panel is historical is the substance; saying WHEN is
     * the refinement.</p>
     */
    briefWrittenAt: (when: string) => `As recorded when this brief was written, ${when}.`,
    briefWrittenAtUnknown: "As recorded when this brief was written.",
    dailyLimit: (nextAllowed: string | null) =>
      nextAllowed
        ? `This buyer has reached today's research limit. The next run is allowed from ${nextAllowed}.`
        : "This buyer has reached today's research limit. Try again tomorrow.",
    invalidMoney: "Enter a whole-dollar amount, or leave it blank.",
    provenance: {
      specialist: "Edited by you",
      proposedByAi: "Proposed by Phoenix",
      biography: "From the biography",
      prequalification: "From the prequalification",
      fromField: (field: string) => `From the buyer record (${field})`,
      fromFieldSplit: (field: string) => `From the buyer record (${field}), split`,
      interview: (question: string) => `From the interview (${question})`,

      /**
       * 🔴 <b>KEPT, AND NO LONGER A PER-ROW CAPTION.</b> `provenanceCaption`
       * returns null for an absent label now, so nothing renders this under a
       * row. It survives because the aggregate note below still has to name the
       * case in words, and because deleting a string that eight rows used to
       * render would make the change look larger than it is in a `git log -S`.
       */
      none: "Not on file",

      /**
       * ONE note for the whole brief, in place of a caption under every row.
       *
       * <p>The nouns are phrased to sit inside the sentence below rather than
       * to stand alone, which is why they are lowercase and carry their own
       * articles: "Sourced from the buyer record and the interview" reads; a
       * list of Title Case labels does not.</p>
       */
      kinds: {
        specialist: "your edits",
        proposedByAi: "Phoenix",
        biography: "the biography",
        prequalification: "the prequalification",
        buyerRecord: "the buyer record",
        interview: "the interview",
        other: "elsewhere on the record",
      } as Record<string, string>,

      /**
       * <p>The unattributed count is stated only when there is one, and it is
       * stated as a count rather than repeated per row: eight rows each saying
       * "Not on file" is eight interruptions carrying one fact, and the fact is
       * not even about the search — it is about how much of the buyer record was
       * filled in before the brief was built.</p>
       *
       * <p>A brief with no recorded provenance at all renders the second
       * sentence alone. A brief with every field sourced renders the first
       * alone. Both are real states and neither should produce a dangling
       * conjunction.</p>
       */
      summary: (sources: string[], unattributed: number): string => {
        const parts: string[] = [];
        if (sources.length > 0) {
          const list =
            sources.length === 1
              ? sources[0]
              : `${sources.slice(0, -1).join(", ")} and ${sources[sources.length - 1]}`;
          parts.push(`Sourced from ${list}.`);
        }
        if (unattributed > 0) {
          parts.push(
            unattributed === 1
              ? "1 field has no recorded source."
              : `${unattributed} fields have no recorded source.`,
          );
        }
        return parts.join(" ");
      },
    },
  },

  history: {
    requestedBy: (name: string | null) => (name ? `by ${name}` : "by a consultant"),
    sources: (succeeded: number, attempted: number) =>
      `${succeeded} of ${attempted} sources answered`,
    candidates: (found: number, shown: number) => `${found} found · ${shown} shown`,
    open: (when: string) => `Open the run from ${when}`,

    /** 🔴 Clef's records are NOT its candidates: the figure counts every record
     * the vendor delivered, including the rows the mapper dropped as delisted or
     * non-USD, because that is what the daily allowance is spent in. */
    clefRecords: (used: number) => `${used} Clef record${used === 1 ? "" : "s"}`,

    /** What Clef's FREE count call said the filter matched, against what the one
     * charged page brought back. Saying only "40 candidates" would hide a capped
     * page behind a thin market, which is the whole reason the count is bought
     * separately. `approximate` when the count call had to drop a filter
     * parameter (RA3), so the number is for a wider search than the one asked. */
    clefShowingOf: (shown: number, matched: number, approximate: boolean) =>
      `showing the ${shown} newest of ${approximate ? "approximately " : ""}${matched}`,
  },

  status: {
    Queued: "Queued",
    Running: "Running",
    Completed: "Completed",
    CompletedPartial: "Completed (partial)",
    Failed: "Failed",
    Cancelled: "Cancelled",
    /** A COMPLETED run whose Pull more was accepted and is waiting for a worker
     * (R9). Nothing has been bought yet, so it says queued rather than
     * pulling. */
    PullQueued: "Pull queued",
    /** A COMPLETED run buying its next batch — not a first run, which is why it
     * does not read "Running". */
    Pulling: "Pulling more",
    /** A COMPLETED run applying a refinement a specialist typed. It says
     * "Refining" rather than "Pulling more" because a refinement may buy
     * nothing at all, and the surface must not promise a batch. */
    Refining: "Refining",
  } satisfies Record<BuyerResearchRunStatus, string>,

  outcome: {
    succeeded: "Answered",
    empty: "Nothing found",
    failed: "Failed",
    skipped: "Skipped",
  } satisfies Record<BuyerResearchSourceOutcome, string>,

  sellerFinancing: {
    required: "Required",
    preferred: "Preferred",
    any: "Any",
  } satisfies Record<BuyerResearchSellerFinancing, string>,

  card: {
    openListing: "Open listing",
    recommended: "Recommended",
    notScored: "Not scored",
    /**
     * 🔴 <b>A TOMBSTONE, NOT A PLACEHOLDER. Nothing renders this.</b>
     *
     * <p>Its one reader was `moneyOrNotStated`, and that helper is retired: SS7
     * put an em-dash in the money columns ({@link researchCopy.results.missingMoney})
     * and the prose form lost its last production call site. The string is kept
     * because it is what the columns must NEVER show, and
     * `ResearchCandidateCard.test.tsx` names it in exactly that negative
     * assertion — deleting it here would delete the counter-example along with
     * the value.</p>
     *
     * <p>Retiring it outright is a two-line change: this key, and that one
     * assertion. Both belong in a change that can edit the card's test.</p>
     */
    notStated: "Not stated",
    buyerFit: "Buyer fit",
    programFit: "Program fit",
    contactVia: (source: string) => `Contact via ${source}`,
    linkOnly: (source: string) => `Open on ${source} — not read by Phoenix`,
    possibleDuplicate: (name: string) => `Possible duplicate of ${name}`,
    attached: "Attached",
    attachedOpen: "Open in Associated listings",
    dismissed: "Dismissed",
    alsoSeenAt: (n: number) => `Also seen on ${n} other ${n === 1 ? "site" : "sites"}`,
    sellerFinancingQuote: "Seller financing:",
    attach: "Attach",
    /** D1 — the disabled control an attached row shows in Attach's place. */
    attachedButton: "Attached",
    attachTitle: "Attach this listing",
    attachReuse: (name: string) => `Reuse the existing listing ${name} and tie it to this buyer.`,
    attachCreate: "Create a listing from this candidate and tie it to this buyer.",
    attachConfirm: "Attach",
    attachedToast: (name: string, reused: boolean) =>
      reused ? `${name} was already on file and is now on this buyer's list.` : `${name} is now on this buyer's list.`,
    dismiss: "Dismiss",
    dismissTitle: "Dismiss this candidate",
    dismissMessage:
      "It will stay dismissed for this buyer on every future run. You can undismiss it from the results.",
    dismissReason: "Reason (optional)",
    dismissReasonPlaceholder: "Why it does not fit",
    dismissConfirm: "Dismiss",
    dismissedToast: "Dismissed.",

    // ---- Clef (D7 / D13 / D14) ------------------------------------------
    /** 🔴 D13 — byte-identical to `BuyerResearchCopy.FiguresUnverified` on the
     * server, which writes the same sentence into the created listing's note.
     * A listing and the card it came from must not disclaim differently.
     * `clefDisclaimerParity.test.ts` reads the C# const and pins it. */
    figuresUnverified: "Figures as published by the source; not verified by Phoenix.",
    /** The action, offered only when `clefListingId` is non-null. */
    getBrokerContact: "Get broker contact",
    getBrokerContactHint: "Clef holds the listing broker's details. Fetching them costs one record.",
    brokerContactNoneOnFile: "Clef had no broker contact for this listing.",
    /** No marketplace URL at all — the listing is reachable only through Clef. */
    noPublicLink: "No public link — this listing is only on Clef.",
    geographyUnknown: "Location not published",
    lowConfidenceIndustry: "Industry is Clef's best guess",
    cashFlowBasis: {
      sde: "SDE",
      cashFlow: "as published",
      ebitda: "EBITDA",
    } as Record<string, string>,

    // ---- Layer B carriers (WS5) -----------------------------------------
    //
    // 🔴 EVERY ONE OF THESE IS NULL ON EVERY ROW WRITTEN BEFORE THE COLUMNS
    // EXISTED. There is no backfill, so a label rendered unconditionally reads
    // as an empty cell on the whole historical population — worse than not
    // rendering it. Each is gated at the call site, and two are gated HARDER
    // than null because their populated value is the unremarkable one:
    // `status` renders only when it is not `active` (the mapper drops delisted
    // rows, so `active` is every row it keeps), and `currency` only when it is
    // not USD (non-USD rows are dropped at the mapper, so USD is every row it
    // keeps). A badge that says "active" on every card is noise a reader learns
    // to stop seeing, which is how a real `delisted` goes unread.

    /** The vendor's finer classification, appended to the industry line. */
    subIndustry: (value: string) => `sub-industry: ${value}`,
    /** Asset sale or stock sale — materially different for lending, which is
     * why it earns a badge rather than a cell. */
    dealType: (value: string) => `Deal type: ${value}`,
    /** Null on every row today: `ClefApiClient.ReadInt` still requires a JSON
     * number for a field Appendix A types as a STRING. Rendered anyway, because
     * the gate is what makes it correct the day that changes. */
    employees: (value: string) => `${value} employees`,
    /** When the vendor last saw the listing CHANGE — rendered as a date, never
     * as "listed", which is a different field. */
    lastChange: "Listing last changed",
    /** Only ever rendered for a value that is NOT `active`. */
    status: (value: string) => `Listing status: ${value}`,
    /** Only ever rendered for a value that is NOT USD. The figures above are
     * stated in it, so an unannounced non-USD figure reads as dollars. */
    currency: (value: string) => `Figures in ${value}`,
    /** Clef's own tags, rendered as chips from the stored JSON array. An
     * unparseable or non-array blob renders NOTHING rather than a raw string —
     * the column is the vendor's payload and a surface must not print it
     * verbatim at a reader. */
    tagsHeading: "Tags",
  },

  flag: {
    yes: "yes",
    no: "no",
    unknown: "unknown",
  } satisfies Record<BuyerResearchFlagValue, string>,

  /**
   * The verdict word a fit check renders, standing where a three-colour pill
   * used to.
   *
   * <p>🔴 <b>These are not `flag` above with capital letters.</b> `flag` is the
   * wire vocabulary — the literal the server sends — and it is lowercase
   * because it is an identifier. These are the words a specialist reads, and
   * they exist separately so that renaming what a reader sees can never change
   * what the client compares against.</p>
   *
   * <p>"Not stated" rather than "Unknown" because the reader's question is
   * about the LISTING, not about Phoenix's confidence: on every check that can
   * return it, the cause is that nobody published the figure the comparison
   * needed. "Unknown" invites the reading that Phoenix looked and could not
   * tell.</p>
   */
  fitVerdict: {
    yes: "Yes",
    no: "No",
    unknown: "Not stated",
  } satisfies Record<BuyerResearchFlagValue, string>,

  buyerFitLabels: {
    industryMatch: "Industry",
    inPriceBand: "In price band",
    withinGeographyOrRelocatable: "Geography",
    cashFlowClearsFloor: "Cash flow floor",
    revenueClearsFloor: "Revenue floor",
    sellerFinancingMentioned: "Seller financing",
  } as Record<string, string>,

  programFitLabels: {
    // Ruling Q21: a price proxy (a listing states no down payment); the program
    // range is measured on the amount financed, so the label says what it read.
    askingPriceInProgramBand: "Asking price in program range",
    cashFlowPresent: "Cash flow present",
    sellerFinancingMentioned: "Seller financing",
    impliedMultipleWithinCeiling: "Multiple within ceiling",
    /**
     * D — the sanity guard. "no" reads as "the figures this listing published
     * cannot all be true at once", NOT as "the deal looks bad": the server only
     * returns it on an impossibility or on a multiple below where any real
     * transaction lives. The label says "consistent" rather than "plausible"
     * for exactly that reason — consistency is a claim about the figures
     * against each other, which is the only claim available for numbers nobody
     * at Phoenix has verified.
     */
    financialsPlausible: "Figures consistent",
  } as Record<string, string>,

  results: {
    showDismissed: "Show dismissed",
    hideDismissed: "Hide dismissed",
    dismissedHeading: "Dismissed for this buyer",
    dismissedEmpty: "Nothing has been dismissed for this buyer.",
    undismiss: "Undismiss",
    undismissedToast: "Undismissed. It can appear on the next run.",
    dismissedBy: (name: string | null) => (name ? `by ${name}` : ""),
    untitled: "Untitled candidate",

    // ---- the list's own controls (R5, R11, R16, R20) -----------------------
    //
    // 🔴 EVERY SENTENCE HERE DESCRIBES WHAT THE SERVER DOES, and the server is
    // the only party that filters, sorts or pages this list. The search box
    // matches title, city and state and nothing else, so its placeholder says
    // so; a "Show them" link reveals BOTH the blanks and the suspect figures on
    // that field, because `showMissing` keeps both.

    searchLabel: "Search candidates",
    searchPlaceholder: "Search title, city or state",
    fitLabel: "Fit score",
    fitChips: { any: "Any", "50": "50+", "75": "75+" } as Record<"any" | "50" | "75", string>,
    latestOnly: "Latest pull only",
    /** D5 — leave out rows attached to the buyer. Off by default. */
    hideAttached: "Hide attached",
    sortLabel: "Sort candidates by",
    /**
     * One control for key AND direction. Best fit has no direction — it is one
     * fixed order on the server (R3) — so it is a single option rather than a
     * pair whose second half would do nothing.
     */
    sortOptions: {
      fit: "Best fit",
      "asking:desc": "Asking price — highest first",
      "asking:asc": "Asking price — lowest first",
      "cashFlow:desc": "Cash flow — highest first",
      "cashFlow:asc": "Cash flow — lowest first",
      "revenue:desc": "Revenue — highest first",
      "revenue:asc": "Revenue — lowest first",
    } as Record<string, string>,
    moneyFields: { asking: "Asking price", cashFlow: "Cash flow", revenue: "Revenue" } as Record<
      "asking" | "cashFlow" | "revenue",
      string
    >,
    moneyMin: "Min",
    moneyMax: "Max",
    moneyBoundLabel: (label: string, bound: "min" | "max") =>
      `${label} ${bound === "min" ? "minimum" : "maximum"}`,
    moneyInvalid: "Enter a dollar amount.",
    /** R16 — fills the bounds the brief carries and clears the two it has no value for. */
    matchBrief: "Match the buyer's brief",
    /** One active-filter chip per money field. */
    moneyChip: (label: string, min: string | null, max: string | null) =>
      min && max ? `${label}: ${min}–${max}` : min ? `${label}: from ${min}` : `${label}: up to ${max}`,
    missingChip: (label: string) => `${label}: blanks and suspect figures included`,

    /**
     * The count line (D11), between the bar and the table. "Matching" is what
     * the filters above leave; "on this run" is every row the run showed,
     * before any of them — so the two can never contradict each other.
     */
    countLine: (first: number, last: number, matching: number, onRun: number) =>
      matching === 0
        ? `0 matching · ${onRun} on this run`
        : `Showing ${first}–${last} of ${matching} matching · ${onRun} on this run`,

    /**
     * One line per FILTERED money field whose filter hid rows it could not
     * judge. A row with a real figure outside the range is hidden and counted
     * nowhere — it is simply not a match — so this line only ever speaks for a
     * blank or a figure under $1,000, which is what "looks wrong" means (R10).
     */
    hiddenForField: (label: string, missing: number, implausible: number) => {
      const noun = label.toLowerCase();
      const wrong = (n: number) => `${n} ${n === 1 ? "looks" : "look"} wrong`;
      if (missing > 0 && implausible > 0) {
        return `${missing} hidden — no ${noun} published · ${wrong(implausible)}`;
      }
      if (missing > 0) return `${missing} hidden — no ${noun} published`;
      return `${implausible} hidden — the ${noun} looks wrong`;
    },
    showThem: "Show them",
    belowFit: (n: number) => `${n} below the fit threshold`,
    showAllFit: "Show all",
    filteredEmpty: "Nothing on this run matches these filters.",
    clearFilters: "Clear filters",
    detailLoadFailedWhat: "this candidate's scoring",
    listLoadFailedWhat: "this run's candidates",

    /** R6/R8 — a pull's rows, marked on a run that has been pulled at least once. */
    newBadge: "New",
    batch: (n: number) => `Batch ${n}`,

    /**
     * R10 — a figure that looks wrong is FLAGGED, never judged: the row keeps
     * its score and its place. One sentence per reason code, so the tooltip
     * says which figure and why.
     */
    implausibleBadge: "Check figures",
    implausibleReasons: {
      placeholderAsking: "The asking price is under $1,000, which looks like a placeholder.",
      placeholderCashFlow: "The cash flow is under $1,000, which looks like a placeholder.",
      placeholderRevenue: "The revenue is under $1,000, which looks like a placeholder.",
      placeholderEbitda: "The EBITDA is under $1,000, which looks like a placeholder.",
      inconsistent:
        "The figures contradict each other — earnings above revenue, or an asking price under half a year's cash flow.",
    } as Record<string, string>,
    implausibleUnknown: "These figures look wrong.",

    // ---- the compact row (Table and Tile Standard SS4, SS11) --------------

    /**
     * Column headings for the candidate list.
     *
     * <p>Short because the money columns are fixed at 7rem, right-aligned, on a
     * `table-fixed` table (§11), and a heading that wraps pushes the whole header
     * row to two lines for three words nobody needed. "Fit" over "Fit score" for
     * the same reason: the column holds a number out of a hundred and the word
     * "score" is carried by the figure's own shape.</p>
     *
     * <p>There is no Source column. The marketplace rides the listing's
     * sub-line, where it costs no width (§11).</p>
     */
    columns: {
      fit: "Fit",
      listing: "Listing",
      asking: "Asking",
      cashFlow: "Cash flow",
      revenue: "Revenue",
      actions: "",
    },

    /** The row's disclosure, named for what it opens rather than "Expand". */
    expandRow: (title: string) => `Show why ${title} was scored this way`,
    collapseRow: (title: string) => `Hide the scoring for ${title}`,

    /**
     * 🔴 <b>SS7 — a plain missing value is an em-dash and NOTHING else.</b> Not
     * "Not stated", not "N/A", not an empty cell, and above all not a pill: the
     * money columns are the ones a specialist scans down, and a chip in three
     * of twenty rows breaks that scan to say only that a seller chose not to
     * publish a figure.
     *
     * <p>It is NOT the retired `card.notStated`: that was prose meant to sit
     * inside a sentence, and a column cell is not a sentence. That key and its
     * one reader (`moneyOrNotStated`) are gone, so the wrong answer is no
     * longer reachable.</p>
     *
     * <p>🔴 <b>OPEN, AND IT IS AN ACCESSIBILITY DEFECT RATHER THAN A STYLE ONE:
     * this string is rendered as a BARE em-dash, so a screen reader hears
     * punctuation or nothing where a sighted reader sees "no figure
     * published".</b> The design system already answers this — `MissingValue`
     * (`design-system/components/ui/TableValue.tsx`) renders the same em-dash
     * `aria-hidden` beside an `sr-only` "Not recorded", and `ListingsListPage`
     * uses it inside a table, so it is precedented in a dense cell. The brief
     * panel on this very screen renders its unset money bounds through it, so
     * two sibling surfaces on one page currently announce differently.</p>
     *
     * <p>The fix is at the ONE consumer — `ResearchCandidateCard`'s `Money`,
     * which is the only production reader of this key — and retiring the key
     * outright is what would stop the next author reaching for it. Neither is
     * in this workstream's file list; recorded in the PR rather than left here,
     * because a comment beside a defect has no owner and no date.</p>
     */
    missingMoney: "\u2014",

    // ---- the withheld scored set (runs cut before R1) --------------------

    /**
     * 🔴 <b>THE COUNT IS PER RUN, AND THE COPY MUST NOT IMPLY A CAP.</b>
     *
     * <p>Before R1 the shown cut (`MaxShown`, since retired) was applied once per
     * batch, so a run pulled three times had shown sixty candidates and held back
     * whatever each batch ranked beyond its own twenty. A sentence built on "the
     * other twenty" is wrong for every multi-batch run, so the figure is always
     * the counted total the server sends.</p>
     */
    withheldAvailable: (withheld: number) =>
      withheld === 1
        ? "1 more candidate was scored on this run and held back."
        : `${withheld} more candidates were scored on this run and held back.`,

    /**
     * The cost, stated before the press rather than discovered after it.
     *
     * <p>The records are already bought and the scoring is already paid for, so
     * revealing costs nothing today. What it does cost is later: an Ask Phoenix
     * refinement scores the SHOWN rows, so a revealed row is a row every future
     * refinement on this run pays to score. That is the whole reason this is a
     * button rather than a raised cap — the specialist who wants the rest pays
     * for the rest, and nobody else does.</p>
     */
    withheldCost:
      "They are already bought and scored, so showing them costs nothing now. " +
      "Any later Ask Phoenix refinement on this run will score them too.",

    withheldAction: "Show the rest",
    withheldPending: "Showing\u2026",
    withheldRevealed: (revealed: number) =>
      revealed === 1 ? "1 candidate added to the list." : `${revealed} candidates added to the list.`,

    /**
     * The same count, while the run is still working.
     *
     * <p>🔴 <b>SEPARATE COPY RATHER THAN A DISABLED BUTTON.</b> `canReveal` is
     * `settled && count > 0`, so a run that is pulling or refining can be
     * holding back rows it cannot yet release — a batch that finished, under a
     * run that has not. §6.1 makes a state that is merely NOT YET muted prose,
     * and the standard's own rule is that a surface must never render a control
     * that cannot do what it appears to do. Stating the count and the reason is
     * the honest shape; a greyed button is the same claim with a dead
     * affordance attached.</p>
     *
     * <p>It deliberately carries no cost clause. `withheldCost` says what
     * PRESSING costs, and here there is nothing to press.</p>
     */
    withheldNotYet: (withheld: number) =>
      withheld === 1
        ? "1 more candidate has been scored on this run and held back. " +
          "It can be shown once the run finishes."
        : `${withheld} more candidates have been scored on this run and held back. ` +
          "They can be shown once the run finishes.",

    // ---- Ask Phoenix, over the list (decisions 4 and 6) -------------------

    /**
     * 🔴 RF1 — <b>NOTHING MOVES; THINGS DISAPPEAR AND COME BACK.</b> A
     * refinement never reorders the list: it is a lens over the order the wire
     * already sent (RC4), and this is the line that makes the missing rows
     * legible instead of leaving a reader to wonder where twenty-eight
     * candidates went.
     *
     * <p>The specialist's OWN WORDS, verbatim, because decision 5 makes a
     * refinement sticky: a colleague opening this run tomorrow has to be able
     * to read what was asked without asking anybody.</p>
     */
    refinementHidden: (visible: number, hidden: number, text: string) =>
      `${visible} of ${visible + hidden} match “${text}” — ${hidden} hidden.`,

    /** Decision 4 — ONE line, ONE click, and never a delete or a confirm. */
    refinementShowAll: "Show all",

    /**
     * 🔴 BEFORE THE PRESS, beside the control — the module's own rule, the one
     * the revert control's `consequence` states for the same reason.
     *
     * <p>Showing them all takes the lens off, which is the only mechanism there
     * is for restoring a hidden candidate, and taking it off ALSO moves what
     * Pull more would buy (decision 6). A control that changed the pull target
     * silently would be a surprise a specialist finds out about by spending
     * records on the wrong search.</p>
     */
    refinementShowAllConsequence:
      "Showing them all takes this refinement off. Nothing is deleted, and Pull more goes back to this run's original search.",

    /**
     * RF2 — a promoted candidate was persisted-but-invisible before this
     * refinement, matched it, and was SCORED before it was shown. Worth saying:
     * without it a specialist reads a list that grew and assumes something was
     * bought.
     */
    /**
     * 🔴 The run's own empty state says "No candidates were shown for this run"
     * and explains it with "already on this buyer's list, dismissed, or scored
     * too low to show" — every clause of which is FALSE when a refinement is
     * what emptied the list. The run found these listings and showed them; one
     * question narrowed them to nothing, and the way back is one click above.
     */
    refinementMatchedNothing: (text: string) =>
      `Nothing this run found matches “${text}”.`,
    refinementMatchedNothingHint:
      "The listings it found are still here — showing them all brings the whole list back.",

    refinementPromoted: (promoted: number) =>
      promoted === 1
        ? "1 listing this run had held back was scored against it and shown."
        : `${promoted} listings this run had held back were scored against it and shown.`,
  },

  /** "Pull more" — the control at the foot of the candidate list. */
  /**
   * Area C — what the adaptive-search probe changed about a run's query, and
   * the control to put it back.
   *
   * 🔴 <b>EVERY SENTENCE HERE HAS TO SURVIVE THE FACT THAT A REVERT CANNOT
   * UN-BUY ANYTHING.</b> The records are spent, the candidates stay, and what
   * changes is the buyer's NEXT run. Copy that says "undo" without saying what
   * it undoes reads as a refund, which is the one thing this control must never
   * imply.
   */
  transcript: {
    heading: "How this search was narrowed",

    /** The headline, and the two numbers that make the narrowing legible. */
    narrowed: (before: number, after: number) =>
      `Narrowed from ${before.toLocaleString()} matches to ${after.toLocaleString()} before buying anything.`,

    /** Every variant cost nothing — said plainly, because a transcript of
     * twelve attempts otherwise reads like twelve charges. */
    variants: (tried: number) =>
      tried === 1
        ? "One free count call, which spent no records."
        : `${tried.toLocaleString()} free count calls, which spent no records.`,

    /** 🔴 The two rates are rendered TOGETHER and never apart: the fit share is
     * a share of the listings that PUBLISH a multiple, so on its own it
     * overstates the market by exactly the non-publishing remainder. */
    density: (fit: string, publishing: string) =>
      `${fit} of the listings that publish a multiple are inside the program ceiling — and ${publishing} publish one at all.`,

    /**
     * Ruling 7 — the by-product of the three free counts above, and the only
     * measurement Phoenix takes of the assumption its whole filter policy rests
     * on: the vendor's financial bounds are not sent because they are BELIEVED
     * to delete listings that published no figure, and this says whether they
     * do.
     *
     * 🔴 <b>IT STATES ONE MEASUREMENT AND CLAIMS NOTHING FURTHER.</b> One probe
     * on one query, taken with a tolerance that leans toward "they passed", is
     * evidence and not a ruling — so the sentence says what was seen on this
     * search and stops. A reader must not come away thinking the policy has
     * changed, because it has not.
     *
     * Rendered only when the server sent a boolean. Absent means nobody
     * measured — the probe never reached the density step — and an absent
     * measurement must never read as "no".
     */
    nullBound: (passed: boolean) =>
      passed
        ? "On this search, a price or earnings ceiling did not drop the listings that publish no figure."
        : "On this search, a price or earnings ceiling dropped the listings that publish no figure.",

    stepsHeading: "What it tried",
    stepAccepted: "kept",
    stepRejected: (why: string | null) => why ?? "not kept",
    /** A variant the probe refused WITHOUT counting it never reached the
     * vendor, so there is no number for that row — said rather than shown as a
     * blank. */
    stepNotCounted: "not counted",

    /** The probe ran and found nothing worth changing. Not a failure. */
    unchanged: "This search bought exactly what the brief describes.",

    action: "Revert these query changes",
    actionPending: "Reverting…",

    /**
     * 🔴 THE WHOLE POINT OF THE CONTROL'S COPY. It is stated BEFORE the press,
     * beside the button, not in a toast afterwards.
     */
    consequence:
      "Phoenix narrowed this search automatically. Reverting tells the next run for this buyer not to — it cannot un-buy these records, and nothing on this list changes.",

    /**
     * Who already did it — rendered AFTER a `<Timestamp>`, the idiom this module
     * already uses for "when, then what happened" (see the dismissal line in
     * ResearchResults). The stamp is a real instant, so it goes through the
     * tz-aware component rather than being formatted into a sentence here.
     */
    revertedBy: (who: string | null) =>
      who == null
        ? "— these query changes were reverted. The next run for this buyer searches the brief unchanged."
        : `— ${who} reverted these query changes. The next run for this buyer searches the brief unchanged.`,

    /**
     * Keyed on the CODE the server sends, never on its prose. A code this
     * client has not learned falls back to a readable sentence rather than the
     * slug — and never to nothing, because a closed control with no explanation
     * is worse than one whose explanation is general.
     */
    blocked: {
      "nothing to revert": "Nothing narrowed this run's query, so there is nothing to put back.",
      "already reverted": "These query changes have already been reverted.",
      "run not finished": "This run is still working. Wait for it to finish and try again.",
    } as Record<string, string>,
    blockedFallback: "These query changes cannot be reverted.",
  },

  pull: {
    action: "Pull more",
    /** While the REQUEST is in flight. The request only queues the pull (R9) —
     * the worker buys — so the button does not claim to be pulling. */
    actionPending: "Queuing…",

    /**
     * R9 — the pull is QUEUED, and the worker buys. What the control says
     * while it waits, and while it works. Neither names a count or a figure:
     * the page size is on the offer the specialist already read, and what the
     * pull actually bought is the batch line, once it has.
     */
    queued: "Pull more is queued — waiting for a worker.",
    pulling: "Pulling the next batch and scoring it.",
    /** How long this surface has been watching the pull. "So far" because the
     * clock starts when this page first SAW the pull working, which is a lower
     * bound on how long it has run — the server sends no start time for it. */
    elapsed: (label: string) => `${label} so far.`,

    /** A queued pull the worker declined to start (the server writes the reason
     * on a skipped source-log line). The server's own sentence is shown when it
     * sent one; this is only for a line with no text. A skip spends nothing. */
    notStartedFallback: "The pull did not start, and nothing was bought.",
    /** A pull that failed, when the log line carried no sentence of its own. */
    failedFallback: "The pull could not be completed.",
    /** RC8's rule on a FAILED pull: records Clef delivered before it failed were
     * charged, and the line says so rather than letting the failure read as
     * free. Omitted when none were bought. */
    failedCharged: (records: number) =>
      `${records} record${records === 1 ? "" : "s"} Clef delivered still counted against the day's allowance.`,

    /**
     * RC12, the approved string. The number is the SERVER's
     * `pull.recordsPerPull`, never a client-side default: what one pull costs
     * is configuration — every page it buys, not one page — and a client that
     * guessed would quote a figure from a deployment other than this one.
     *
     * "up to" is load-bearing — the source clamps its page size against the
     * allowance Clef reports on the free count call, so the figure is a ceiling
     * rather than a promise.
     */
    available: (records: number) =>
      `Clef has more matches for this brief. Pulling the next ${records} costs up to ${records} records.`,

    /**
     * 🔴 DECISION 6 — <b>PULL MORE CONTINUES THE REFINEMENT.</b> With a
     * refinement applied, the next page is a page of the REFINEMENT's search,
     * not the brief's, and the sentence above would be false at the one moment
     * a specialist is deciding whether to spend on it. Clearing the refinement
     * restores the original search as the pull target, at which point the
     * sentence above is true again.
     *
     * <p>The number is still the SERVER's `pull.recordsPerPull` — the same
     * figure, for the same reason: what one pull costs is configuration.</p>
     */
    availableRefined: (records: number) =>
      `Clef has more matches for this refinement. Pulling the next ${records} costs up to ${records} records.`,

    /** RC1 — what is left of this run's budget, beside the offer. */
    budgetLeft: (usd: string) => `${usd} left of this run's budget.`,

    /**
     * 🔴 RC8 — WHAT THE PULL COST, NOT WHAT SURVIVED IT.
     *
     * <p>The offset feed re-delivers rows across a page boundary and Clef
     * charges for every one, so a page of 20 that yields 16 cards still spent 20
     * records. Saying "16" would be the friendlier number and the wrong one.</p>
     *
     * <p>The middle clauses appear only when they are non-zero — a line reading
     * "0 already on this list" invents a reassurance nobody asked for — but the
     * closing clause is unconditional, because the whole point is that the
     * headline figure is the charge.</p>
     */
    lastBatch: (
      records: number,
      duplicates: number,
      dismissed: number,
      unscored = 0,
      unscoredReason: string | null = null,
    ) => {
      const parts = [`Pulled ${records}`];
      if (duplicates > 0) parts.push(`${duplicates} already on this list`);
      if (dismissed > 0) parts.push(`${dismissed} already dismissed`);
      const allowance = " — all of them still counted against the day's allowance.";
      if (unscored <= 0) return `${parts.join(" · ")}${allowance}`;

      const reason = knownUnscoredReason(unscoredReason);
      const inLine = reason != null ? UNSCORED_IN_BATCH_LINE[reason] : null;
      // 🔴 TWO BUDGETS, TWO SENTENCES (R2). A reason about RECORDS joins the
      // records sentence it is part of; a reason about the MODEL budget, or one
      // this client has no sentence for, is a sentence of its own after it.
      if (inLine?.budget === "records") {
        parts.push(`${unscored} ${inLine.clause}`);
        const after = inLine.after ? ` ${inLine.after}` : "";
        return `${parts.join(" · ")}${allowance}${after}`;
      }
      return `${parts.join(" · ")}${allowance} ${unscoredSentenceFor(unscored, reason)}`;
    },

    /**
     * 🔴 R1/R15 — ROWS THAT WERE BOUGHT AND NEVER SCORED, keyed by the server's
     * reason CODE. Typed as a `Record` over the reason union, so a member added
     * to that union without a sentence is a compile error here. A code the
     * server sends that this client has not learned is NOT a compile error — it
     * gets the bare count (`unscoredGeneric`), never a borrowed sentence.
     *
     * <p>They are never shown unscored and never offered by the reveal — this
     * sentence is the only place they surface.</p>
     */
    unscored: UNSCORED_SENTENCES,

    /** Any other reason the server sends: the count, with no clause. */
    unscoredGeneric: UNSCORED_GENERIC,

    /** The standalone sentence for `count` unscored rows — the source row's
     * disclosure, where there is no "Pulled N" headline to join. */
    unscoredSentence: (count: number, reason: string | null | undefined) =>
      unscoredSentenceFor(count, knownUnscoredReason(reason ?? null)),

    /**
     * R2 — the pull refused because this run's MODEL budget is down to the
     * part kept back for Ask Phoenix. The figure is the SERVER's
     * `pull.reserveUsd`; a caller with no figure uses the map's entry below,
     * which names none. A model-dollar sentence only: Clef's record allowance
     * is a different budget and is never mentioned beside it.
     */
    aiBudgetReserved: (usd: string) =>
      `This run's AI budget is nearly used — ${usd} is kept so Ask Phoenix still works.`,

    /**
     * Why the control is closed. RC2 — the SERVER decides, the client renders a
     * sentence; these are keyed by the blocker CODE so the same map serves a
     * disabled caption and a refused request.
     *
     * 🔴 The two Clef sentences are one server state told apart, and the pair is
     * load-bearing: a null next page with no stored reason means either Clef
     * never ran or Clef ran and recorded no continuation point. `clef did not
     * run` above a source-log row reading `clef · succeeded · 20` is the exact
     * contradiction this control shipped with, so neither sentence may be
     * written to cover both cases.
     *
     * 🔴 TWO of these have NO PRODUCER today — `rank cap reached` and `allowance
     * held back` — and they are here because the server's vocabulary contains
     * them, not because anything emits them. The map is `satisfies
     * Record<BuyerResearchPullBlocker, string>`, so leaving either out would not
     * merely make it look exhaustive, it would fail to compile the moment one
     * gained a producer and the union was read honestly. The sentences are
     * written for the day they arrive.
     */
    blocked: {
      "no more pages": "Clef has no more matches for this brief.",
      "rank cap reached": "This run already holds as many candidates as it can rank.",
      "empty page": "The last page came back empty, so there is nothing further to buy.",
      "clef did not run": "This run has no paged source to buy more from.",
      "clef page unknown":
        "Clef ran on this run, but the run recorded no page to continue from. Run the brief again to start a fresh batch.",
      // 🔴 RF6/RF7 — WHICH BUDGET, AND WHICH LEVER. "Its budget" was ambiguous
      // between Phoenix's per-run MODEL cap and Clef's shared daily RECORD
      // allowance, which is the sentence directly below and resets at midnight;
      // and "narrow or change the brief" pointed at a door that is usually
      // locked, because an unchanged brief is refused inside the re-run window
      // and only five of the seven filter fields can move at all — country is
      // fixed at US and include-unknown-state is a deliberate constant. The
      // three that a specialist can actually turn are named.
      // 🔴 RE8 — AND A FOURTH LEVER, BECAUSE ON ONE BRIEF THE THIRD IS DEAD.
      // `BuildFilter`'s RA2 rule sends NO geography when the brief says the
      // buyer will relocate, and the re-run guard fingerprints the FILTER — so
      // on such a brief the places a specialist edits never reach the query,
      // the fingerprint does not move, and the refusal repeats. Naming the
      // location without naming the relocation answer points at a handle that
      // does not turn. It mirrors `ClefListingSource.ReRunRemedy`, which
      // carries the argument in full.
      "spend cap reached":
        "This run has spent its model budget, so Phoenix cannot pay to rank another batch. A fresh run gets a fresh budget — change the industry, the location, the maximum price or the relocation answer on the brief and run it again, because an unchanged brief is refused inside the re-run window. A brief that says the buyer will relocate searches no geography at all, so on one of those the location is inert until that answer moves.",
      "allowance held back": "Clef's daily allowance is low, so this run is holding the rest back.",
      // R2 — the no-figure form, for a server that sent no `reserveUsd`. With a
      // figure, `aiBudgetReserved` above is used instead.
      "ai budget reserved":
        "This run's AI budget is nearly used — the rest is kept so Ask Phoenix still works.",
      "run busy": "This run is still working.",
      "run not completed": "This run did not complete, so there is no batch to add to.",
    } satisfies Record<BuyerResearchPullBlocker, string>,

    /**
     * A blocker this client has not learned yet. Rendered instead of the code,
     * so a server that gains a reason shows something readable rather than a
     * slug — and never nothing, which would leave a closed control unexplained.
     */
    blockedFallback: "This run cannot buy another batch.",

    /**
     * RF7's second clause, as a SEPARATE sentence appended after the blocked
     * caption rather than folded into it.
     *
     * 🔴 <b>Separate because the map above is a static `Record` and the window
     * is CONFIGURATION.</b> `ClefOptions.MinReRunHours` defaults to 24 and a
     * slot may set anything; a hardcoded "24 hours" inside the map would be
     * redundant on the default and wrong the moment it moved, which is exactly
     * why `ClefListingSource.ReRunRemedy` declines to restate it. The server
     * sends the number on `BuyerResearchPullStateDto.reRunWindowHours`, and a
     * caller renders this ONLY when it did — a null omits the clause instead of
     * guessing.
     */
    reRunWindow: (hours: number) =>
      hours === 1
        ? "The re-run window is 1 hour from the last search."
        : `The re-run window is ${hours} hours from the last search.`,
  },

  /**
   * Ask Phoenix — a refinement a specialist types over a run that has already
   * finished, in their own words.
   *
   * <p>🔴 <b>RF6 — TWO BUDGETS, TWO SENTENCES, NEVER ONE.</b> Records come off
   * Clef's shared daily allowance and reset at midnight UTC; model spend comes
   * off this run's own cap, which is configuration. Every sentence below that
   * names a cost names WHICH budget it comes out of, because telling somebody
   * to wait for midnight over the model cap is telling them to wait for
   * something that will not help.</p>
   *
   * <p>🔴 <b>RF13 — the cost is stated BESIDE THE BUTTON, before the press.</b>
   * Not in a modal, not in a toast afterwards. The whole of decision 1 is that
   * the count is free, the number is on screen, and nothing is bought until
   * somebody clicks — which is worth nothing if the number arrives after the
   * click.</p>
   */
  askPhoenix: {
    heading: "Ask Phoenix",

    /**
     * \U0001F534 THE INPUT'S OWN HEADING, BECAUSE THE INPUT IS NO LONGER BESIDE THE
     * STATE (WS6).
     *
     * <p>The applied lens stays ABOVE the list \u2014 what you are looking at is a
     * filtered list, and a reader who cannot see the filter cannot read the
     * list \u2014 while the thing that SETS it moved to the foot of the list it
     * acts on. That is the owner ruling recorded in `ResearchTab` verbatim:
     * <i>the state stays above the list; the INPUT may sit adjacent to the list
     * or stick to it.</i></p>
     *
     * <p>Two sections both titled "Ask Phoenix" would give a screen-reader user
     * two regions with one name and no way to tell which is which, so the input
     * says what it acts on. It still carries the feature's name, because the
     * place you use a thing is the worst place for its name to disappear.</p>
     */
    inputHeading: "Ask Phoenix about these listings",
    intro:
      "Narrow these results in your own words. Phoenix reads what you asked and says what it would cost before it buys anything.",
    label: "What would you like to change?",
    placeholder: "e.g. only listings with Plumbing in the title",
    action: "Submit",
    actionPending: "Asking Phoenix…",

    /** What Phoenix understood, above its own rationale. */
    understood: "Phoenix read that as:",

    /** The specialist's words, shown back verbatim — decision 5. */
    asked: "You asked:",
    askedBy: (name: string | null) => (name ? `Asked by ${name}` : "Asked by a consultant"),

    confirm: "Confirm and buy",
    /** While the REQUEST is in flight. Confirming only queues the buy (R9) —
     *  the worker buys — so the button does not claim to be buying. */
    confirmPending: "Confirming…",

    /**
     * Discarding a refinement that is waiting for approval. The SAME endpoint
     * as the applied case (`clearRefinement`) with a different label, because
     * the two are different moments: one takes a lens off, the other answers a
     * question nobody has answered yet. Without it a specialist is stuck — the
     * `awaiting confirmation` blocker says answer this one before starting
     * another, and "no" has to be an answer.
     */
    discard: "Discard",
    discardPending: "Discarding…",

    clear: "Clear this refinement",
    clearPending: "Clearing…",

    /**
     * 🔴 THE CHAIN, WHEN THERE IS ONE. Only rendered where the server sent a
     * `summary`, which it withholds for a lone refinement — a one-step chain IS
     * the instruction on the line above, and repeating it would imply a
     * composition that is not there.
     *
     * The specialist's own words at every step, never Phoenix's reading of
     * them: `understood` is where the paraphrase goes, and a chain built out of
     * paraphrases would be a record of what Phoenix thought rather than of what
     * was asked.
     */
    chain: "Still in force:",

    /**
     * 🔴 The consequence, beside the button and before the press. Clearing
     * un-buys nothing — the records a confirmed re-query spent are spent, and
     * the candidates it bought stay on the list. What changes is the lens and,
     * per decision 6, what Pull more would buy next.
     */
    clearConsequence:
      "Clearing takes the lens off and brings every hidden listing back. It cannot un-buy records this refinement already spent, and Pull more goes back to this run's original search.",

    /**
     * 🔴 THE SAME BUTTON, AND A DIFFERENT SENTENCE, BECAUSE IT DOES MORE. A
     * refinement that inherited steps carries the WHOLE chain, so clearing it
     * takes every instruction off at once — not the last one typed. A
     * specialist reading the unqualified sentence over a two-step chain would
     * reasonably expect to be left with the first step, and would instead be
     * back at the unfiltered run. There is no remove-one-step control, and this
     * is the sentence that stops the button implying one.
     */
    clearChainConsequence:
      "Clearing takes off every instruction above at once, not just the last one, and brings every hidden listing back. It cannot un-buy records these refinements already spent, and Pull more goes back to this run's original search.",

    // ---- what it costs (RF2 / RF6, stated BEFORE the press) ---------------

    /** 🔴 `cost.isFree` is the SERVER's answer and the only one. A client that
     *  re-derived it would be free to call a scoring call free. */
    costFree: "Applying this costs nothing — no records, and no scoring call.",

    /** The free count, which is the whole of decision 1. `approximate` means
     *  the vendor refused a parameter and the count retried without it, so the
     *  number is for a WIDER search than the one asked. */
    costMatched: (matched: number, approximate: boolean) =>
      `${approximate ? "Approximately " : ""}${matched.toLocaleString()} listing${
        matched === 1 ? "" : "s"
      } match, counted for free.`,

    /** Budget one: Clef's shared daily RECORD allowance. */
    costRecords: (records: number) =>
      `Confirming buys up to ${records.toLocaleString()} record${
        records === 1 ? "" : "s"
      } off Clef's daily allowance.`,

    /** Budget two: this run's MODEL cap. Named apart from the records, always. */
    costScoring: (usd: string) =>
      `It also costs ${usd} of this run's model budget, for one scoring call.`,
    costScoringOnly: (usd: string) =>
      `Applying this costs ${usd} of this run's model budget, for one scoring call.`,

    costBudgetLeft: (usd: string) => `${usd} left of this run's budget.`,

    /**
     * 🔴 The honest answer when this browser does not hold the quote.
     *
     * <p>The cost arrives on the classify response and rides nowhere else —
     * `BuyerResearchRefinementStateDto` carries no cost — so a colleague who
     * opens a run holding somebody else's unanswered refinement has the
     * MEASURED match count (which is on the sticky record) and no spend figure.
     * Saying so is the only truthful move: inventing a number from the run's
     * page size would quote a figure nobody measured.</p>
     */
    costNotQuoted:
      "Phoenix quoted this to whoever asked it; this browser does not hold that figure. Discard it and ask again to see the cost before anything is bought.",

    /** A re-query classified before its count came back. */
    costNotCounted: "Phoenix has not counted the matches for this one yet.",

    // ---- vocabulary -------------------------------------------------------

    /** What one refinement turned out to be, in a specialist's terms. */
    kind: {
      "in place": "Filters the listings this run already holds. Nothing is bought.",
      "re-query": "Asks Clef for a different set of listings.",
      both: "Filters what this run holds and asks Clef for more.",
      /** 🔴 The second sentence is not a hedge, it is the whole truth about
       *  this kind today. A re-rank spends a real scoring call and records what
       *  it thought, and NOTHING on the wire carries those scores — the
       *  refinement hides nothing and promotes nothing, so the list a specialist
       *  is looking at does not visibly change. Saying only the first sentence
       *  promises an outcome the product cannot show them, on the one line they
       *  read before approving. Delete it when the scores reach the card, not
       *  before. */
      "re-rank":
        "Scores every listing this run holds against what you asked. "
        + "The scores are recorded against this refinement; the list itself does not change yet.",
      refused: "Phoenix will not act on this one.",
    } satisfies Record<BuyerResearchRefinementKind, string>,

    status: {
      classifying: "Phoenix is reading what you asked…",
      "awaiting confirmation": "Waiting for you to approve the spend",
      /** Confirmed and QUEUED (R9): the worker has not started. It names no buy,
       *  because a `both` whose filter half could not be serialised buys
       *  nothing and still applies its text half. */
      confirmed: "Confirmed — waiting to start",
      applying: "Applying it…",
      applied: "Applied",
      refused: "Not acted on",
      failed: "Failed",
      cleared: "Cleared",
    } satisfies Record<BuyerResearchRefinementStatus, string>,

    /** What it did, once it did it. Zero clauses are dropped: "0 hidden"
     *  invents a reassurance nobody asked for, the same rule the pull's last
     *  batch line follows. */
    outcomeCounts: (visible: number, hidden: number, promoted: number) => {
      const parts = [`${visible} shown`];
      if (hidden > 0) parts.push(`${hidden} hidden`);
      if (promoted > 0) parts.push(`${promoted} promoted`);
      return parts.join(" · ");
    },

    /** What a confirmed re-query actually spent, after the fact. */
    spent: (records: number) =>
      `${records.toLocaleString()} record${records === 1 ? "" : "s"} spent.`,

    // ---- history (RF13 — a SIBLING of the transcript) ---------------------

    historyHeading: "Earlier refinements",
    historyShow: "Show earlier refinements",
    historyHide: "Hide earlier refinements",

    // ---- refusals ---------------------------------------------------------

    /**
     * Why Ask Phoenix will not take a refinement right now, keyed by the
     * blocker CODE so one map serves a closed control and a refused request.
     *
     * 🔴 <b>RF6 lives here.</b> `spend cap reached` names CONFIGURATION and
     * says waiting will not help; `allowance held back` names MIDNIGHT. They
     * are two budgets and the two sentences must never collapse into one, or a
     * specialist waits overnight for a cap that resets never.
     */
    blocked: {
      "run busy": "This run is still working. Wait for it to finish, then ask again.",
      "run not completed": "This run did not complete, so there are no results to refine.",
      "nothing to refine":
        "This run found no listings, so there is nothing to filter and nothing to score.",
      // 🔴 RF7 — THE LEVER MUST BE ONE THE READER CAN PULL, AND THIS AND THE
      // PULL CONTROL'S OWN `spend cap reached` SENTENCE ARE READ ON THE SAME TAB.
      // This said "that cap is configuration — raising it is the lever", which
      // points at `PerRunCapUsd`: a server option with NO client surface
      // (`grep -rn PerRunCap src/PhoenixCapital.Web/src` is empty), addressed to
      // a specialist who cannot change it, and silent on the one lever they can
      // pull. Twelve lines above, the pull map's sentence for the SAME blocker
      // had already been fixed under RF7 to name a fresh run — so one state gave
      // two remedies and the nearer one was the dead end.
      //
      // RF6 still lives in the second clause: this cap is PER RUN and waiting
      // never moves it, which is what keeps it distinct from the record
      // allowance below, which resets at midnight UTC. Do not drop "waiting will
      // not move it" while making the lever real — both halves are the ruling.
      "spend cap reached":
        "This run has spent its model budget, so Phoenix cannot pay for the scoring a refinement needs. Waiting will not move it — this cap is per run, not per day. A fresh run gets a fresh budget: change the industry, the location, the maximum price or the relocation answer on the brief and run it again, because an unchanged brief is refused inside the re-run window. A brief that says the buyer will relocate searches no geography at all, so on one of those the location is inert until that answer moves.",
      "allowance held back":
        "Clef's shared daily record allowance is low, so Phoenix is holding the rest back. It resets at midnight UTC.",
      "awaiting confirmation":
        "A refinement is already waiting for you to approve its spend. Answer that one first.",
    } satisfies Record<BuyerResearchRefinementBlocker, string>,

    /**
     * A blocker this client has not learned yet — rendered instead of the slug,
     * and never instead of nothing: a closed control with no explanation is
     * worse than one whose explanation is general.
     */
    blockedFallback: "Phoenix cannot take a refinement on this run right now.",
  },

  /**
   * Conversational search — building the query with the specialist, before
   * anything is bought.
   *
   * 🔴 THE REGISTER THIS BLOCK HAS TO HOLD: a specialist who names
   * something Clef cannot filter on gets a ROUTE, never an apology. "I can't
   * ask Clef to filter on cash flow, so I'll narrow the buy by industry,
   * location and price ceiling — then filter these results on cash flow for
   * free once they're here. Want me to?" A shrug is a failed turn.
   */
  conversation: {
    heading: "Build the search",
    /** The opening line, before the first count lands (RE6). */
    opening: "Reading this buyer's record…",
    placeholder:
      "Tell me what to look for — “just plumbing”, “the western states”, “nothing over $2M”",
    send: "Send",
    sendPending: "Thinking…",

    /**
     * 🔴 The count line is the promise this feature rests on, so it says
     * WHAT it measured rather than showing a bare number.
     */
    matches: (n: number) => (n === 1 ? "1 match" : `${n.toLocaleString()} matches`),
    /**
     * 🔴 THE LABEL LEADS, and it is not a style choice. The labels this is
     * handed are the SERVER'S — `ConversationCopy.CurrentQueryLabel` ("This
     * search") and `ProposedQueryLabel` ("With that change") — and the template
     * used to read `${n} matches for ${label}`, which composed them into
     * "515 matches for With that change". Neither label is a noun phrase a
     * "for" can govern; both are fine as a subject.
     *
     * The coupling is real and nothing enforces it: this template and those
     * constants have to compose into a sentence, and they live in two
     * languages. Changing either means reading the other.
     */
    matchesFor: (n: number, label: string) =>
      `${label}: ${n === 1 ? "1 match" : `${n.toLocaleString()} matches`}`,
    approximate:
      "approximately — Clef could not apply one of the filters, so this counts a wider search",
    counting: "Counting…",
    /** Zero is an answer, and it cost nothing to have asked. */
    noMatches:
      "No matches. Nothing was spent finding that out — widen the industry, the places or the price ceiling and I'll count it again.",

    /**
     * RE4 — the per-turn count budget, said as an OFFER. A conversation that
     * refuses because it ran out of FREE calls is the failure this removes.
     */
    unmeasured: (queries: string[]) =>
      queries.length === 1
        ? `I did not count ${queries[0]} — say so and I will.`
        : `I did not count these: ${queries.join(", ")}. Say which and I will.`,

    routeAccept: "Yes, do both",
    routeDecline: "Just the search",

    /** RE6 — the count arrives a beat after the drawer, never blocking it. */
    briefReady: "Here is what I'd search. Counting it now…",

    /**
     * RE2 — ⚠️ the resume ceiling, stated rather than left to be noticed.
     * The preference row is capped at 8192 CHARACTERS, so a long conversation
     * comes back with its OLDEST turns dropped and a specialist must be TOLD
     * which part is missing.
     *
     * 🔴 **THE SECOND CLAUSE IS LOAD-BEARING AND MUST NOT BE TRIMMED.** The
     * brief is carried whole on `resume.brief` and only the TRANSCRIPT is
     * shed, so the thing they are about to run is intact even when the history
     * is not — and a specialist who reads "messages were dropped" without it
     * has every reason to assume their query was dropped too, and to rebuild
     * work that is sitting in front of them.
     */
    resumed: "Picked up where you left off.",
    resumedTruncated: (dropped: number) =>
      dropped === 1
        ? "Older messages were dropped to fit — 1 earlier turn from this conversation isn't here. Your search itself came back in full."
        : `Older messages were dropped to fit — ${dropped} earlier turns from this conversation aren't here. Your search itself came back in full.`,

    /** Who said a line. `note` is a fact about the conversation and is drawn
     * muted, so it carries no speaker label at all. */
    speakerYou: "You",
    speakerPhoenix: "Phoenix",

    /**
     * 🔴 **THE STALE-COUNT LINE IS THE WHOLE SYMPTOM THIS FEATURE EXISTS TO
     * DISSOLVE, SAID OUT LOUD.** A count describes the query it was taken
     * against; edit the brief by hand afterwards and the number on screen is
     * about a different search. Rather than re-counting on every keystroke —
     * which would be a vendor request per character — the count is marked
     * stale the moment the working brief stops matching what was counted, and
     * the specialist is offered a fresh one.
     */
    countStale: "That count was for the query before your last edit.",
    countAgain: "Count it again",

    /** The brief cannot be counted while a money field will not parse: there is
     * nothing to send, and a count of a half-typed query would be worse than
     * none. */
    briefInvalid: "Fix the highlighted amount and I'll count it.",

    /** A turn that failed at the transport. The instruction is kept in the box
     * so nobody retypes it. */
    turnFailed: (message: string) => `That did not go through: ${message}`,
    countFailed: (message: string) => `I could not count that: ${message}`,

    /**
     * Ruling 1 — what ACCEPTING a route actually does, said in the words of
     * what actually happens rather than as a promise nothing keeps.
     *
     * 🔴 **THE CLAUSE RIDES THE BRIEF'S OWN `notes` FIELD, which is the same
     * channel Ask Phoenix's re-rank uses** (`BuildRefinementScores` appends the
     * instruction to `brief.Notes` and says so in place). So it reaches the
     * ranking prompt as a stated preference — a real effect on the order of
     * the list — and it lands on the run's own record, where it is on screen
     * beside the results the specialist then filters. What it is NOT is a hard
     * vendor filter, and none of this copy says it is: the free in-place filter
     * is the post-run Ask Phoenix action the server's own route sentence
     * offers.
     */
    routeAccepted: (clause: string) =>
      `Kept: ${clause}. It rides with the search as a stated preference, and you can filter these results on it for free from Ask Phoenix once they are here.`,
    routeDeclined: (clause: string) => `Dropped: ${clause}. The search itself is unchanged.`,
    routePending: (clause: string) => `After this run: ${clause}.`,

    /** A direct field edit, recorded so the next turn reasons about the truth. */
    noteEdited: (field: string) => `You changed ${field} yourself.`,

    /** RE8 — both guards, on screen while building rather than at RUN. */
    runsLeft: (used: number, allowed: number) =>
      allowed - used === 1
        ? `1 more run for this buyer today (${used} of ${allowed} used).`
        : `${allowed - used} more runs for this buyer today (${used} of ${allowed} used).`,
    runsNoneLeft: (nextAllowed: string | null) =>
      nextAllowed
        ? `This buyer has used all of today's runs. The next one is allowed from ${nextAllowed}.`
        : "This buyer has used all of today's runs.",
    /**
     * 🔴 <b>IT IS NOT A REFUSAL, AND THE OLD COPY SAID IT WAS.</b> This used to
     * be `reRunWouldRefuse` and read "…or wait out the window", which a reader
     * takes as "the Run button will not work". Nothing refuses. The re-run
     * guard is a SOURCE SKIP evaluated at execution time, inside
     * `ClefListingSource`: the run starts, is queued, is executed, and Clef —
     * the only source that returns listings in this environment — reports
     * `SourceResult.Skip`. A skip is neutral, so the run does not even read as
     * partial. The specialist gets an empty list and no account of why.
     *
     * <p>So the sentence says what will happen. It keeps both halves RF7 asked
     * for — the four levers and the window — because those are still the way
     * out; what changes is that it no longer promises a door that will hold.</p>
     */
    reRunWouldSkipClef: (ranAt: string, windowHours: number) =>
      `This is the same search that already ran for this buyer at ${ranAt}. Running it again will start a run, but Clef is skipped inside the ${windowHours}-hour window, so it comes back with nothing. Change the industry, the places, the price ceiling or the relocation answer first.`,

    /**
     * 🔴 THE SENTENCE THAT STOPS THE COUNT BEING READ AS A PROMISE, on the one
     * path where it is not one.
     *
     * <p>A run the specialist has not settled in conversation is still probed:
     * `ClefListingSource` converges a broad query, re-counts against the
     * narrowed filter and buys THAT. The figure above is therefore the
     * population before narrowing — true about the brief, and not about the
     * purchase. Saying so costs one line and is the difference between a number
     * that informs and a number that misleads.</p>
     *
     * <p>It names the remedy rather than only the caveat, because the remedy is
     * the entire feature: a turn sets `settledInConversation`, the server
     * suppresses the probe, and the number becomes the one the buy honours.</p>
     */
    mayNarrowBeforeBuying:
      "Phoenix narrows a broad query before buying, so the records may come from fewer matches than this. Tell it what you are looking for and this number becomes the one it buys against.",

    run: "Run it as proposed",
    runPending: "Starting…",

    /**
     * 🔴 R8 — what ACCEPTING a route does, said where the offer is made.
     *
     * <p>The owner's ruling: "Accepting the route does not install it, and no
     * member here could… the post-accept toast is accurate, and the offer
     * sentence overstates." The offer sentence is the SERVER's, approved
     * verbatim and pinned against the capability catalogue on disk, so it is
     * not rewritten here. This is the caveat that sits beside it, and it says
     * what happens rather than what we wish happened: a clause is appended to
     * the brief's own notes.</p>
     *
     * <p>That clause is real — `BuildRefinementScores` appends the brief's
     * notes to the ranking prompt, so it moves the ORDER of the list — and it
     * is not a filter. The honest weak sentence ships.</p>
     */
    routeCaveat:
      "Accepting adds the clause to this brief's notes, which Phoenix reads when it ranks. It does not install a filter — nothing here can. Once the records are in, Ask Phoenix can filter them on it for free.",
    runWithCount: (n: number) =>
      n === 1 ? "Run it — 1 match" : `Run it — ${n.toLocaleString()} matches`,
  },

  /**
   * Quick search — a sentence typed on the Research tab, read by the server and
   * run at once with no brief and no confirmation step.
   *
   * <p>🔴 <b>Every count here is the SERVER's.</b> The default list returns only
   * the rows that matched what was typed, so "37 match what you typed" and the
   * cards on screen are one number read twice, never two numbers that happen to
   * agree.</p>
   */
  quick: {
    label: "Quick search",
    placeholder: "Quick search, e.g. HVAC companies for sale in Austin Texas",
    /** Under the box while the sentence is read and the run is queued. */
    pendingHint: "Phoenix is reading your search…",
    /** The box's own submit — Enter does the same. */
    submit: "Quick search",
    submitPending: "Searching…",
    /** The run bar / history label: the typed sentence, cut on a word. */
    historyLabel: (text: string) => `Quick search · “${text}”`,
    /** The heading over the how-it-read line, in place of the brief. */
    readHeading: "How Phoenix read your search",

    /** Q5 — the counts line. `found` is omitted when Clef gave no count. */
    countsLine: (c: {
      found: number | null;
      scored: number;
      matches: number;
      unconfirmed: number;
      noMatch: number;
    }) =>
      [
        c.found == null ? null : `Clef found ${c.found.toLocaleString()}`,
        `${c.scored.toLocaleString()} scored`,
        `${c.matches.toLocaleString()} match what you typed`,
        `${c.unconfirmed.toLocaleString()} couldn't confirm`,
        `${c.noMatch.toLocaleString()} didn't match`,
      ]
        .filter((p): p is string => p != null)
        .join(" · "),

    /** Q1/Q2 — the reveal links beside "Show dismissed". */
    showUnconfirmed: (n: number) => `Show ${n.toLocaleString()} couldn't confirm`,
    hideUnconfirmed: "Hide couldn't confirm",
    showNoMatch: (n: number) => `Show ${n.toLocaleString()} didn't match what you typed`,
    hideNoMatch: "Hide didn't match",

    /** A row's badge when it did not match what was typed. */
    didNotMatch: "Didn't match",

    /** A7 — the expanded card's per-criterion verdicts. */
    criteriaHeading: "What you typed",
    criterionValue: {
      yes: "Yes",
      no: "No",
      unknown: "Couldn't confirm",
    } as Record<string, string>,

    /** The empty list when nothing matched and the rest are behind the links. */
    noMatches: "Nothing matched everything you typed.",
    noMatchesHint: "The listings that couldn't be confirmed or didn't match are behind the links above.",
  },
} as const;
